import { useState, useEffect } from 'react';
import { Users, TrendingUp, Mail, Globe, Package, Send, Flag, MapPin, AlertTriangle, FileText } from 'lucide-react';
import { adminAxios } from './AdminLayout';

export default function AdminDashboard() {
  var [stats, setStats] = useState(null);

  useEffect(function() {
    adminAxios().get('/api/admin/dashboard').then(function(res) { setStats(res.data); });
  }, []);

  if (!stats) return <div className="animate-pulse font-manrope text-slate-500">Loading dashboard...</div>;

  var cards = [
    { label: 'Total Clients', value: stats.total_clients, icon: Users, color: 'bg-blue-500' },
    { label: 'Subscribed', value: stats.subscribed_clients, icon: Mail, color: 'bg-green-500' },
    { label: 'Unsubscribed', value: stats.unsubscribed_clients, icon: Users, color: 'bg-red-400' },
    { label: 'Website Enquiries', value: stats.website_enquiries, icon: Globe, color: 'bg-purple-500' },
    { label: 'Newsletter', value: stats.newsletter_subscribers, icon: TrendingUp, color: 'bg-orange-500' },
    { label: 'Manual Entries', value: stats.manual_entries, icon: Users, color: 'bg-slate-500' },
    { label: 'Total Offers', value: stats.total_offers, icon: Package, color: 'bg-cyan-500' },
    { label: 'Offers Sent', value: stats.sent_offers, icon: Send, color: 'bg-emerald-500' },
  ];

  var m = stats.market || {};
  var q = stats.quotes || {};
  var ca = stats.campaigns || {};

  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">Dashboard</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(function(card, i) {
          return (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={'dashboard-card-' + i}>
              <div className="flex items-center gap-3">
                <div className={'w-10 h-10 rounded-xl flex items-center justify-center ' + card.color}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-space text-2xl font-bold text-slate-900">{card.value}</p>
                  <p className="font-manrope text-xs text-slate-500">{card.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Client Markets Section */}
      <div className="mb-8" data-testid="market-intelligence">
        <h3 className="font-outfit text-lg font-bold text-slate-900 mb-4">Client Markets</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-800">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-space text-2xl font-bold text-slate-900">{stats.total_clients}</p>
                <p className="font-manrope text-xs text-slate-500">Total Clients</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm" data-testid="market-uk">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-500">
                <Flag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-space text-2xl font-bold text-blue-700">{m.uk_total || 0}</p>
                <p className="font-manrope text-xs text-blue-500">UK</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm" data-testid="market-international">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-500">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-space text-2xl font-bold text-emerald-700">{m.international_total || 0}</p>
                <p className="font-manrope text-xs text-emerald-500">International</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-500">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-space text-2xl font-bold text-slate-900">{m.gb_mainland || 0}</p>
                <p className="font-manrope text-xs text-slate-500">UK Mainland</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-500">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-space text-2xl font-bold text-slate-900">{m.northern_ireland || 0}</p>
                <p className="font-manrope text-xs text-slate-500">Northern Ireland</p>
              </div>
            </div>
          </div>
        </div>

        {/* Needs Review */}
        {(m.needs_review || 0) > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="market-needs-review">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-manrope text-sm font-semibold text-amber-800">{m.needs_review} client{m.needs_review > 1 ? 's' : ''} requiring classification review</p>
              <p className="font-manrope text-xs text-amber-600">These clients could not be automatically classified. Review their country data in the Clients section.</p>
            </div>
          </div>
        )}
      </div>

      {/* Activity Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-500">
              <Send className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-outfit text-sm font-bold text-slate-900">Campaigns</p>
            </div>
          </div>
          <div className="flex justify-between">
            <div><p className="font-space text-xl font-bold text-slate-900">{ca.total || 0}</p><p className="font-manrope text-xs text-slate-500">Total</p></div>
            <div><p className="font-space text-xl font-bold text-green-600">{ca.completed || 0}</p><p className="font-manrope text-xs text-green-500">Completed</p></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-500">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-outfit text-sm font-bold text-slate-900">Quotes</p>
            </div>
          </div>
          <div className="flex justify-between">
            <div><p className="font-space text-xl font-bold text-slate-900">{q.total || 0}</p><p className="font-manrope text-xs text-slate-500">Total</p></div>
            <div><p className="font-space text-xl font-bold text-orange-500">{q.new || 0}</p><p className="font-manrope text-xs text-orange-500">New</p></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-teal-500">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-outfit text-sm font-bold text-slate-900">Market Split</p>
            </div>
          </div>
          {stats.total_clients > 0 ? (
            <div>
              <div className="flex gap-1 h-4 rounded-full overflow-hidden mb-2">
                <div className="bg-blue-500 transition-all" style={{width: ((m.uk_total || 0) / stats.total_clients * 100) + '%'}}></div>
                <div className="bg-emerald-500 transition-all" style={{width: ((m.international_total || 0) / stats.total_clients * 100) + '%'}}></div>
                {(m.needs_review || 0) > 0 && <div className="bg-amber-400 transition-all" style={{width: ((m.needs_review || 0) / stats.total_clients * 100) + '%'}}></div>}
              </div>
              <div className="flex justify-between text-xs font-manrope text-slate-500">
                <span className="text-blue-600">{Math.round((m.uk_total || 0) / stats.total_clients * 100)}% UK</span>
                <span className="text-emerald-600">{Math.round((m.international_total || 0) / stats.total_clients * 100)}% International</span>
              </div>
            </div>
          ) : (
            <p className="font-manrope text-xs text-slate-400">No clients yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
